import AddIcon from "@mui/icons-material/Add";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import {
  ManagedUser,
  StagedModel,
  StagedParent,
} from "src/app/models/screens/userManagement.model";
import UserManagementScreenViewModel from "src/app/screens/userManagement/userManagement.viewmodel";
import styles from "src/app/styles/screens/userManagement.style";

const roleColor = (
  role: ManagedUser["role"],
): "primary" | "secondary" | "default" => {
  if (role === "Model") return "primary";
  if (role === "Parent") return "secondary";
  return "default";
};

const UserManagementScreenView: React.FC = () => {
  const vm = UserManagementScreenViewModel();

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="Users" />
      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <Grid container justifyContent="center" spacing={3}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              User Management
            </Typography>
          </Grid>
          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            <Paper elevation={0} sx={styles.tabsContainer}>
              {vm.userRole === "Admin" && (
                <Tabs
                  value={vm.selectedTab}
                  onChange={vm.onTabChanged}
                  variant="fullWidth"
                  scrollButtons="auto"
                >
                  <Tab
                    icon={<PersonIcon />}
                    iconPosition="start"
                    label="All Users"
                  />
                  <Tab
                    icon={<GroupAddIcon />}
                    iconPosition="start"
                    label="Add Users"
                  />
                  <Tab
                    icon={<PersonAddAlt1Icon />}
                    iconPosition="start"
                    label="Add Guest"
                  />
                </Tabs>
              )}
              <Divider />
              {vm.userRole === "Admin" && vm.selectedTab === 0 && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.sectionTitle}>All Users</Typography>
                  <TextField
                    fullWidth
                    label="Search users"
                    value={vm.userSearchText}
                    onChange={(e) => vm.setUserSearchText(e.target.value)}
                    sx={styles.searchBox}
                  />
                  {vm.isSearchingUsers ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mt: 3 }}
                    >
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <List disablePadding>
                      {vm.allUsers.length === 0 && (
                        <Typography sx={styles.noItemsText}>
                          No users found.
                        </Typography>
                      )}
                      {vm.allUsers.map((user: ManagedUser) => {
                        const isExpanded = vm.expandedUserId === user.id;
                        const related = vm.relatedUsersMap[user.id] ?? [];
                        const isLoadingThis = vm.loadingRelatedId === user.id;
                        const editForm = vm.userEditForms[user.id];
                        const isLoadingDetails =
                          vm.loadingUserDetailsId === user.id;
                        const isSavingThis = vm.savingUserId === user.id;
                        const isApplyingStorageThis =
                          vm.applyingStorageLimitUserId === user.id;
                        const isDeactivatingThis =
                          vm.deactivatingUserId === user.id;
                        const isSuspendingThis =
                          vm.suspendingUserId === user.id;

                        return (
                          <Box key={user.id}>
                            <ListItemButton
                              sx={[
                                styles.userListItem,
                                {
                                  backgroundColor: isExpanded
                                    ? "rgba(25,118,210,0.06)"
                                    : "transparent",
                                },
                              ]}
                              onClick={() => vm.onUserRowClicked(user)}
                            >
                              <ListItemText
                                primary={`${user.firstName} ${user.lastName}`}
                                secondary={
                                  user.phoneNumber
                                    ? `${user.email} · ${user.phoneNumber}`
                                    : user.email
                                }
                              />
                              <Chip
                                size="small"
                                label={user.role}
                                color={roleColor(user.role)}
                                sx={styles.roleChip}
                              />
                            </ListItemButton>
                            <Collapse in={isExpanded} unmountOnExit>
                              <Box sx={styles.relatedUsersContainer}>
                                {isLoadingThis || isLoadingDetails ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <>
                                    {related.length === 0 && (
                                      <Typography sx={styles.noItemsText}>
                                        No linked accounts.
                                      </Typography>
                                    )}

                                    {related.map((rel: ManagedUser) =>
                                      user.role === "Guest" ? (
                                        <Box
                                          key={rel.id}
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            borderRadius: "8px",
                                            mb: "4px",
                                            px: 1,
                                            py: 0.5,
                                          }}
                                        >
                                          <Box sx={{ flex: 1 }}>
                                            <Typography>
                                              {rel.firstName} {rel.lastName}
                                            </Typography>
                                            <Typography
                                              sx={{
                                                fontSize: 13,
                                                color: "text.secondary",
                                              }}
                                            >
                                              {rel.phoneNumber
                                                ? `${rel.email} · ${rel.phoneNumber}`
                                                : rel.email}
                                            </Typography>
                                          </Box>

                                          <Chip
                                            size="small"
                                            label={rel.role}
                                            color={roleColor(rel.role)}
                                            sx={styles.roleChip}
                                          />

                                          <IconButton
                                            color="error"
                                            size="small"
                                            loading={
                                              vm.removingAccessibleUserKey ===
                                              `${user.id}-${rel.id}`
                                            }
                                            disabled={
                                              vm.removingAccessibleUserKey ===
                                              `${user.id}-${rel.id}`
                                            }
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              vm.onRemoveAccessibleUser(
                                                user.id,
                                                rel.id,
                                              );
                                            }}
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      ) : (
                                        <ListItemButton
                                          key={rel.id}
                                          sx={styles.relatedUserItem}
                                          dense
                                          disableRipple
                                        >
                                          <ListItemText
                                            primary={`${rel.firstName} ${rel.lastName}`}
                                            secondary={
                                              rel.phoneNumber
                                                ? `${rel.email} · ${rel.phoneNumber}`
                                                : rel.email
                                            }
                                          />
                                          <Chip
                                            size="small"
                                            label={rel.role}
                                            color={roleColor(rel.role)}
                                            sx={styles.roleChip}
                                          />
                                        </ListItemButton>
                                      ),
                                    )}

                                    <Box sx={styles.relatedActionsRow}>
                                      {user.role === "Parent" && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<LinkIcon />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            vm.openLinkDialog(
                                              user,
                                              "addChildToParent",
                                            );
                                          }}
                                        >
                                          Add / Link Child
                                        </Button>
                                      )}
                                      {user.role === "Model" && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<LinkIcon />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            vm.openLinkDialog(
                                              user,
                                              "addParentToModel",
                                            );
                                          }}
                                        >
                                          Add / Link Parent
                                        </Button>
                                      )}

                                      {user.role === "Guest" && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<LinkIcon />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            vm.openAccessibleUsersDialog(user);
                                          }}
                                        >
                                          Manage Accessible Users
                                        </Button>
                                      )}
                                    </Box>
                                    {editForm && (
                                      <Paper
                                        elevation={0}
                                        sx={{
                                          mt: 2,
                                          p: 2,
                                          borderRadius: 3,
                                          border: "1px solid rgba(0,0,0,0.08)",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: 700,
                                            mb: 2,
                                          }}
                                        >
                                          Edit User
                                        </Typography>

                                        <Grid container spacing={2}>
                                          <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                              fullWidth
                                              label="Email"
                                              type="email"
                                              value={editForm.email}
                                              onChange={(e) =>
                                                vm.updateUserEditField(
                                                  user.id,
                                                  "email",
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          </Grid>
                                          <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                              fullWidth
                                              label="Phone Number"
                                              value={editForm.phoneNumber}
                                              onChange={(e) =>
                                                vm.updateUserEditField(
                                                  user.id,
                                                  "phoneNumber",
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          </Grid>
                                          <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                              fullWidth
                                              label="First Name"
                                              value={editForm.firstName}
                                              onChange={(e) =>
                                                vm.updateUserEditField(
                                                  user.id,
                                                  "firstName",
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          </Grid>

                                          <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                              fullWidth
                                              label="Last Name"
                                              value={editForm.lastName}
                                              onChange={(e) =>
                                                vm.updateUserEditField(
                                                  user.id,
                                                  "lastName",
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          </Grid>

                                          {user.role === "Model" && (
                                            <>
                                              <Grid size={{ xs: 12, sm: 6 }}>
                                                <FormControl>
                                                  <FormLabel>Gender</FormLabel>
                                                  <RadioGroup
                                                    row
                                                    value={editForm.gender}
                                                    onChange={(e) =>
                                                      vm.updateUserEditField(
                                                        user.id,
                                                        "gender",
                                                        e.target.value,
                                                      )
                                                    }
                                                  >
                                                    <FormControlLabel
                                                      value="Male"
                                                      control={<Radio />}
                                                      label="Male"
                                                    />
                                                    <FormControlLabel
                                                      value="Female"
                                                      control={<Radio />}
                                                      label="Female"
                                                    />
                                                  </RadioGroup>
                                                </FormControl>
                                              </Grid>

                                              <Grid size={{ xs: 12, sm: 6 }}>
                                                <DatePicker
                                                  label="Date of Birth"
                                                  value={
                                                    editForm.dateOfBirth
                                                      ? dayjs(
                                                          editForm.dateOfBirth,
                                                        )
                                                      : null
                                                  }
                                                  onChange={(d) =>
                                                    vm.updateUserEditField(
                                                      user.id,
                                                      "dateOfBirth",
                                                      d
                                                        ? d.format("YYYY-MM-DD")
                                                        : "",
                                                    )
                                                  }
                                                  disableFuture
                                                  slotProps={{
                                                    textField: {
                                                      fullWidth: true,
                                                    },
                                                  }}
                                                />
                                              </Grid>
                                            </>
                                          )}

                                          {user.role === "Parent" && (
                                            <Grid size={{ xs: 12 }}>
                                              <TextField
                                                fullWidth
                                                label="Address"
                                                multiline
                                                minRows={2}
                                                value={editForm.address}
                                                onChange={(e) =>
                                                  vm.updateUserEditField(
                                                    user.id,
                                                    "address",
                                                    e.target.value,
                                                  )
                                                }
                                              />
                                            </Grid>
                                          )}

                                          <Grid size={{ xs: 12 }}>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                              }}
                                            >
                                              <Button
                                                variant="contained"
                                                startIcon={<SaveIcon />}
                                                loading={isSavingThis}
                                                onClick={() =>
                                                  vm.onSaveUserEdits(user)
                                                }
                                              >
                                                Save Changes
                                              </Button>
                                            </Box>
                                          </Grid>

                                          <Grid size={{ xs: 12 }}>
                                            <Divider />
                                          </Grid>

                                          <Grid size={{ xs: 12, sm: 8 }}>
                                            <TextField
                                              fullWidth
                                              label="Cloud Storage Limit"
                                              type="number"
                                              value={
                                                editForm.cloudStorageLimitGb
                                              }
                                              onChange={(e) =>
                                                vm.updateUserEditField(
                                                  user.id,
                                                  "cloudStorageLimitGb",
                                                  e.target.value,
                                                )
                                              }
                                              InputProps={{
                                                endAdornment: (
                                                  <InputAdornment position="end">
                                                    GB
                                                  </InputAdornment>
                                                ),
                                              }}
                                            />
                                          </Grid>

                                          <Grid size={{ xs: 12, sm: 4 }}>
                                            <Button
                                              fullWidth
                                              variant="outlined"
                                              loading={isApplyingStorageThis}
                                              sx={{ height: "56px" }}
                                              onClick={() =>
                                                vm.onApplyStorageLimit(user)
                                              }
                                            >
                                              Apply Limit
                                            </Button>
                                          </Grid>

                                          <Grid size={{ xs: 12 }}>
                                            <Divider />
                                          </Grid>

                                          <Grid size={{ xs: 12, sm: 8 }}>
                                            <TextField
                                              fullWidth
                                              label="Suspend For"
                                              type="number"
                                              value={editForm.suspendDays}
                                              onChange={(e) =>
                                                vm.updateUserEditField(
                                                  user.id,
                                                  "suspendDays",
                                                  e.target.value,
                                                )
                                              }
                                              InputProps={{
                                                endAdornment: (
                                                  <InputAdornment position="end">
                                                    days
                                                  </InputAdornment>
                                                ),
                                              }}
                                            />
                                          </Grid>

                                          <Grid size={{ xs: 12, sm: 4 }}>
                                            <Button
                                              fullWidth
                                              variant="outlined"
                                              color="warning"
                                              loading={isSuspendingThis}
                                              sx={{ height: "56px" }}
                                              onClick={() =>
                                                vm.onSuspendUser(user)
                                              }
                                            >
                                              Suspend
                                            </Button>
                                          </Grid>

                                          <Grid size={{ xs: 12 }}>
                                            <Button
                                              variant="outlined"
                                              color="error"
                                              loading={isDeactivatingThis}
                                              onClick={() =>
                                                vm.onDeactivateUser(user)
                                              }
                                            >
                                              Deactivate Account
                                            </Button>
                                          </Grid>
                                        </Grid>
                                      </Paper>
                                    )}
                                  </>
                                )}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </List>
                  )}
                </Box>
              )}
              {vm.userRole === "Admin" && vm.selectedTab === 1 && (
                <Box sx={styles.selectedTab}>
                  <Box sx={styles.addButtonsRow}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={vm.openModelDialog}
                    >
                      Add Model
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={vm.openParentDialog}
                    >
                      Add Parent
                    </Button>
                  </Box>
                  {vm.stagedParents.length > 0 && (
                    <>
                      <Typography sx={styles.sectionTitle}>Parents</Typography>
                      {vm.stagedParents.map((p: StagedParent) => (
                        <Paper
                          key={p._tempId}
                          elevation={0}
                          sx={styles.stagedCard}
                        >
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              sx={styles.removeCardButton}
                              onClick={() => vm.removeStagedParent(p._tempId)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Typography sx={styles.stagedCardTitle}>
                            {p.firstName} {p.lastName}
                          </Typography>
                          <Typography sx={styles.stagedCardSubtitle}>
                            {p.email}
                          </Typography>
                          <Typography sx={styles.stagedCardSubtitle}>
                            {p.phoneNumber}
                          </Typography>
                          <Typography sx={styles.stagedCardSubtitle}>
                            {p.address}
                          </Typography>
                        </Paper>
                      ))}
                    </>
                  )}
                  {vm.stagedModels.length > 0 && (
                    <>
                      <Typography
                        sx={[styles.sectionTitle, { marginTop: "16px" }]}
                      >
                        Models
                      </Typography>
                      {vm.stagedModels.map((m: StagedModel) => {
                        const isMinor =
                          dayjs().diff(dayjs(m.dateOfBirth), "year") < 18;
                        return (
                          <Paper
                            key={m._tempId}
                            elevation={0}
                            sx={styles.stagedCard}
                          >
                            <Tooltip title="Remove">
                              <IconButton
                                size="small"
                                sx={styles.removeCardButton}
                                onClick={() => vm.removeStagedModel(m._tempId)}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Typography sx={styles.stagedCardTitle}>
                              {m.firstName} {m.lastName}
                            </Typography>
                            <Typography sx={styles.stagedCardSubtitle}>
                              {m.email}
                            </Typography>
                            {m.phoneNumber && (
                              <Typography sx={styles.stagedCardSubtitle}>
                                {m.phoneNumber}
                              </Typography>
                            )}
                            <Typography sx={styles.stagedCardSubtitle}>
                              DOB: {m.dateOfBirth} · {m.gender}
                            </Typography>
                            {isMinor && (
                              <Typography sx={styles.warningText}>
                                Minor account
                              </Typography>
                            )}
                          </Paper>
                        );
                      })}
                    </>
                  )}
                  {vm.stagedModels.length === 0 &&
                    vm.stagedParents.length === 0 && (
                      <>
                        <Typography sx={styles.noItemsText}>
                          No users staged yet. Use the buttons above to add
                          models or parents.
                        </Typography>
                        <Typography sx={styles.noItemsText}>
                          Users you add at once will be connected with a
                          child/parent relationship.
                        </Typography>
                      </>
                    )}
                  <Box sx={styles.saveButtonRow}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      loading={vm.isSavingBatch}
                      disabled={
                        vm.stagedModels.length === 0 &&
                        vm.stagedParents.length === 0
                      }
                      onClick={vm.onSaveBatch}
                    >
                      Save All
                    </Button>
                  </Box>
                </Box>
              )}
              {((vm.userRole === "Admin" && vm.selectedTab === 2) ||
                (vm.userRole !== "Admin" && vm.selectedTab === 0)) && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.sectionTitle}>
                    Create Guest User
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={vm.guestFirstName}
                        onChange={(e) => vm.setGuestFirstName(e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={vm.guestLastName}
                        onChange={(e) => vm.setGuestLastName(e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={vm.guestEmail}
                        onChange={(e) => vm.setGuestEmail(e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        value={vm.guestPhoneNumber}
                        onChange={(e) => vm.setGuestPhoneNumber(e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Password"
                        value={vm.guestPassword}
                        onChange={(e) => vm.setGuestPassword(e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Auto-generate password">
                                <IconButton
                                  onClick={() =>
                                    vm.setGuestPassword(vm.generatePassword())
                                  }
                                  edge="end"
                                >
                                  <AutorenewIcon />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          variant="contained"
                          startIcon={<PersonAddAlt1Icon />}
                          loading={vm.isCreatingGuest}
                          disabled={
                            !vm.guestFirstName.trim() ||
                            !vm.guestLastName.trim() ||
                            !vm.guestEmail.trim() ||
                            !vm.guestPhoneNumber.trim() ||
                            !vm.guestPassword.trim()
                          }
                          onClick={vm.onCreateGuest}
                        >
                          Create Guest
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>
      <Dialog
        open={vm.isModelDialogOpen}
        onClose={vm.closeModelDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Model</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={vm.modelFirstName}
                onChange={(e) => vm.setModelFirstName(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={vm.modelLastName}
                onChange={(e) => vm.setModelLastName(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={vm.modelEmail}
                onChange={(e) => vm.setModelEmail(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={vm.modelPhoneNumber}
                onChange={(e) => vm.setModelPhoneNumber(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Password"
                value={vm.modelPassword}
                onChange={(e) => vm.setModelPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Auto-generate password">
                        <IconButton
                          onClick={() =>
                            vm.setModelPassword(vm.generatePassword())
                          }
                          edge="end"
                        >
                          <AutorenewIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <DatePicker
                label="Date of Birth"
                value={vm.modelDob}
                onChange={(d) => vm.setModelDob(d)}
                disableFuture
                slotProps={{ textField: { fullWidth: true } }}
              />
              {vm.isModelMinor() && (
                <Typography sx={styles.warningText}>
                  ⚠ This model is under 18. Make sure you add at least one
                  parent.
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl>
                <FormLabel>Gender</FormLabel>
                <RadioGroup
                  row
                  value={vm.modelGender}
                  onChange={(e) => vm.setModelGender(e.target.value)}
                >
                  <FormControlLabel
                    value="Male"
                    control={<Radio />}
                    label="Male"
                  />
                  <FormControlLabel
                    value="Female"
                    control={<Radio />}
                    label="Female"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={vm.closeModelDialog}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button onClick={vm.onConfirmAddModel} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={vm.isParentDialogOpen}
        onClose={vm.closeParentDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Parent</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={vm.parentFirstName}
                onChange={(e) => vm.setParentFirstName(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={vm.parentLastName}
                onChange={(e) => vm.setParentLastName(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={vm.parentEmail}
                onChange={(e) => vm.setParentEmail(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={vm.parentPhoneNumber}
                onChange={(e) => vm.setParentPhoneNumber(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Address"
                multiline
                minRows={2}
                value={vm.parentAddress}
                onChange={(e) => vm.setParentAddress(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Password"
                value={vm.parentPassword}
                onChange={(e) => vm.setParentPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Auto-generate password">
                        <IconButton
                          onClick={() =>
                            vm.setParentPassword(vm.generatePassword())
                          }
                          edge="end"
                        >
                          <AutorenewIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={vm.closeParentDialog}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button onClick={vm.onConfirmAddParent} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={vm.isLinkDialogOpen}
        onClose={vm.closeLinkDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {vm.linkDialogMode === "addParentToModel"
            ? `Add / Link Parent → ${vm.linkDialogAnchorUser?.firstName} ${vm.linkDialogAnchorUser?.lastName}`
            : `Add / Link Child → ${vm.linkDialogAnchorUser?.firstName} ${vm.linkDialogAnchorUser?.lastName}`}
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={vm.linkDialogTab}
            onChange={vm.onLinkDialogTabChanged}
            sx={styles.dialogTabsContainer}
          >
            <Tab value="existing" label="Link Existing" />
            <Tab
              value="new"
              label={
                vm.linkDialogMode === "addParentToModel"
                  ? "Create New Parent"
                  : "Create New Model"
              }
            />
          </Tabs>
          {vm.linkDialogTab === "existing" && (
            <Box>
              <TextField
                fullWidth
                label={
                  vm.linkDialogMode === "addParentToModel"
                    ? "Search parents"
                    : "Search models"
                }
                value={vm.pickerSearchText}
                onChange={(e) => vm.setPickerSearchText(e.target.value)}
                sx={styles.pickerSearchBox}
              />
              {vm.isLoadingPickerUsers ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <List disablePadding sx={styles.pickerList}>
                  {vm.pickerUsers.length === 0 && (
                    <Box sx={{ p: 2 }}>
                      <Typography sx={styles.noItemsText}>
                        No users found.
                      </Typography>
                    </Box>
                  )}
                  {vm.pickerUsers.map((u: ManagedUser) => (
                    <ListItemButton
                      key={u.id}
                      sx={styles.pickerListItem}
                      onClick={() => vm.onLinkExistingUser(u)}
                      disabled={vm.isLinkingExisting}
                    >
                      <ListItemText
                        primary={`${u.firstName} ${u.lastName}`}
                        secondary={
                          u.phoneNumber
                            ? `${u.email} · ${u.phoneNumber}`
                            : u.email
                        }
                      />
                      {vm.isLinkingExisting && (
                        <CircularProgress size={16} sx={{ ml: 1 }} />
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          )}
          {vm.linkDialogTab === "new" && (
            <Grid container spacing={2}>
              {vm.linkDialogMode === "addChildToParent" && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={vm.newModelFirstName}
                      onChange={(e) => vm.setNewModelFirstName(e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={vm.newModelLastName}
                      onChange={(e) => vm.setNewModelLastName(e.target.value)}
                    />
                  </Grid>
                </>
              )}
              {vm.linkDialogMode === "addParentToModel" && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={vm.newParentName}
                      onChange={(e) => vm.setNewParentName(e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={vm.newParentLastName}
                      onChange={(e) => vm.setNewParentLastName(e.target.value)}
                    />
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={vm.newAccountEmail}
                  onChange={(e) => vm.setNewAccountEmail(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={vm.newAccountPhoneNumber}
                  onChange={(e) => vm.setNewAccountPhoneNumber(e.target.value)}
                />
              </Grid>
              {vm.linkDialogMode === "addParentToModel" && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Address"
                    multiline
                    minRows={2}
                    value={vm.newParentAddress}
                    onChange={(e) => vm.setNewParentAddress(e.target.value)}
                  />
                </Grid>
              )}
              {vm.linkDialogMode === "addChildToParent" && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <DatePicker
                      label="Date of Birth"
                      value={vm.newModelDob}
                      onChange={(d) => vm.setNewModelDob(d)}
                      disableFuture
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControl>
                      <FormLabel>Gender</FormLabel>
                      <RadioGroup
                        row
                        value={vm.newModelGender}
                        onChange={(e) => vm.setNewModelGender(e.target.value)}
                      >
                        <FormControlLabel
                          value="Male"
                          control={<Radio />}
                          label="Male"
                        />
                        <FormControlLabel
                          value="Female"
                          control={<Radio />}
                          label="Female"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Password"
                  value={vm.newAccountPassword}
                  onChange={(e) => vm.setNewAccountPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Auto-generate password">
                          <IconButton
                            onClick={() =>
                              vm.setNewAccountPassword(vm.generatePassword())
                            }
                            edge="end"
                          >
                            <AutorenewIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={vm.closeLinkDialog}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          {vm.linkDialogTab === "new" && (
            <Button
              onClick={vm.onCreateNewAndLink}
              variant="contained"
              loading={vm.isCreatingNewAndLinking}
            >
              Create & Link
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={vm.isAccessibleUsersDialogOpen}
        onClose={vm.closeAccessibleUsersDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Manage Accessible Users → {vm.accessibleUsersAnchorGuest?.firstName}{" "}
          {vm.accessibleUsersAnchorGuest?.lastName}
        </DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            label="Search users"
            value={vm.accessibleUsersSearchText}
            onChange={(e) => vm.setAccessibleUsersSearchText(e.target.value)}
            sx={styles.pickerSearchBox}
          />

          {vm.isLoadingAccessibleUsersOptions ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List disablePadding sx={styles.pickerList}>
              {vm.accessibleUsersOptions.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography sx={styles.noItemsText}>
                    No users found.
                  </Typography>
                </Box>
              )}

              {vm.accessibleUsersOptions.map((u: ManagedUser) => (
                <ListItemButton
                  key={u.id}
                  sx={styles.pickerListItem}
                  onClick={() => vm.onAddAccessibleUser(u)}
                  disabled={vm.addingAccessibleUserId === u.id}
                >
                  <ListItemText
                    primary={`${u.firstName} ${u.lastName}`}
                    secondary={
                      u.phoneNumber ? `${u.email} · ${u.phoneNumber}` : u.email
                    }
                  />
                  {vm.addingAccessibleUserId === u.id && (
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={vm.closeAccessibleUsersDialog}
            variant="outlined"
            color="inherit"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponentView
        isError={vm.isSnackbarError}
        isOpen={vm.isDisplayingSnackbar}
        message={vm.snackbarMessage}
        onClose={() => vm.setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default UserManagementScreenView;
